import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Button } from '@/components/ui';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { DynamicForm } from '@/components/common/DynamicForm';
import { SortableNavigationTree } from '@/components/navigation/SortableNavigationTree';
import { AccessControl, usePermissions } from '@/components/navigation/AccessControl';
import { useLanguage } from '@/state/language/LanguageProvider';
import { NavigationCRUDService } from '@/services/navigationCRUD';
import { 
  navigationModuleEntity, 
  navigationScreenEntity,
  navigationNodeEntity
} from '@/config/entities';
import { 
  PlusIcon, 
  ArrowsUpDownIcon,
  TableCellsIcon,
  EyeIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

type ViewMode = 'tree' | 'modules' | 'screens' | 'nodes';
type EntityType = 'module' | 'screen' | 'node';

const NavigationManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const permissions = usePermissions();

  
  // Determine view mode based on URL
  const getViewModeFromUrl = (): ViewMode => {
    if (location.pathname.includes('/navigation/hierarchy')) {
      return 'tree';
    } else if (location.pathname.includes('/navigation/modules')) {
      return 'modules';
    } else if (location.pathname.includes('/navigation/screens')) {
      return 'screens';
    } else if (location.pathname.includes('/navigation/nodes')) {
      return 'nodes'; // Nodes view should show table of individual nodes
    }
    return 'tree'; // Default to hierarchy view
  };
  
  const [viewMode, setViewMode] = useState<ViewMode>(getViewModeFromUrl());
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [currentEntity, setCurrentEntity] = useState<EntityType>('module');
  const [formKey, setFormKey] = useState(0); // Force re-render when switching entities
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, any[]>>({});
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);
  const [selectedScreenIds, setSelectedScreenIds] = useState<string[]>([]);

  // Update view mode when URL changes
  useEffect(() => {
    const newViewMode = getViewModeFromUrl();
    setViewMode(newViewMode);
    
    // Set current entity based on view mode
    if (newViewMode === 'modules') {
      setCurrentEntity('module');
    } else if (newViewMode === 'screens') {
      setCurrentEntity('screen');
    } else if (newViewMode === 'nodes') {
      setCurrentEntity('node');
    }
  }, [location.pathname]);

  // Load dynamic options for select fields
  const loadDynamicOptions = async () => {
    try {
      // Load all modules (active and inactive) for parent module dropdown
      // This ensures all modules are available for selection, not just active ones
      const modulesResponse = await NavigationCRUDService.getAllModules();
      const modules = modulesResponse.data || [];
      
      const moduleOptions = modules.map((module: any) => ({
        value: module.id,
        label: module.display_name + (module.is_active === false ? ' (Inactive)' : '')
      }));

      // Load screens for screen_id field
      const screensResponse = await NavigationCRUDService.getScreens();
      const screens = screensResponse.data || [];
      
      const screenOptions = screens.map((screen: any) => ({
        value: screen.id,
        label: screen.display_name
      }));

      // Resource types are already defined in the entity configuration
      // No need to load them dynamically from API


      setDynamicOptions({
        parent_module_id: moduleOptions,
        module_id: moduleOptions,
        screen_id: screenOptions
      });
    } catch (error) {
      console.error('Error loading dynamic options:', error);
    }
  };

  useEffect(() => {
    loadDynamicOptions();
  }, []);

  // Get current entity configuration
  const getCurrentEntity = () => {
    let baseEntity;
    if (currentEntity === 'module') {
      baseEntity = navigationModuleEntity;
    } else if (currentEntity === 'screen') {
      baseEntity = navigationScreenEntity;
    } else if (currentEntity === 'node') {
      baseEntity = navigationNodeEntity;
    } else {
      baseEntity = navigationModuleEntity;
    }
    
    // Inject dynamic options into the entity configuration
    const entityWithOptions = {
      ...baseEntity,
      fields: baseEntity.fields?.map(field => {
        if (field.key === 'parent_module_id' || field.key === 'module_id' || field.key === 'screen_id') {
          const options = dynamicOptions[field.key] || [];
          return {
            ...field,
            options: options
          };
        }
        return field;
      })
    };
    
    return entityWithOptions;
  };

  // Get current service
  const getCurrentService = () => {
    switch (currentEntity) {
      case 'module':
        return {
          getAll: NavigationCRUDService.getModules,
          getById: NavigationCRUDService.getModuleById,
          create: NavigationCRUDService.createModule,
          update: NavigationCRUDService.updateModule,
          delete: NavigationCRUDService.deleteModule,
          copy: NavigationCRUDService.copyModule
        };
      case 'screen':
        return {
          getAll: NavigationCRUDService.getScreens,
          getById: NavigationCRUDService.getScreenById,
          create: NavigationCRUDService.createScreen,
          update: NavigationCRUDService.updateScreen,
          delete: NavigationCRUDService.deleteScreen,
          copy: NavigationCRUDService.copyScreen
        };
      case 'node':
        return {
          getAll: NavigationCRUDService.getNodes,
          getById: NavigationCRUDService.getNodeById,
          create: NavigationCRUDService.createNode,
          update: NavigationCRUDService.updateNode,
          delete: NavigationCRUDService.deleteNode,
          copy: NavigationCRUDService.copyNode
        };
      default:
        return {
          getAll: NavigationCRUDService.getModules,
          getById: NavigationCRUDService.getModuleById,
          create: NavigationCRUDService.createModule,
          update: NavigationCRUDService.updateModule,
          delete: NavigationCRUDService.deleteModule,
          copy: NavigationCRUDService.copyModule
        };
    }
  };

  const handleCreate = (entityType: EntityType) => {
    setCurrentEntity(entityType);
    setEditingItem(null);
    setShowForm(true);
    setFormKey(prev => prev + 1);
  };

  const handleEdit = (item: any) => {
    if (item?.type === 'module' || item?.type === 'screen' || item?.type === 'node') {
      setCurrentEntity(item.type);
    }
    setEditingItem(item);
    setShowForm(true);
    setFormKey(prev => prev + 1);
  };



  const handleToggleStatus = async (item: any) => {
    try {
      const service = getCurrentService();
      const newStatus = !item.is_active;
      await service.update(item.id, { is_active: newStatus });
      toast.success(`${getCurrentEntity().displayNameSingular} ${newStatus ? t('navigation.activated') : t('navigation.deactivated')} successfully`);
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error(t('navigation.failedToToggleStatus'));
    }
  };

  const handleHardDelete = async (item: any) => {
    if (!window.confirm(t('navigation.confirmPermanentDelete', { entityName: getCurrentEntity().displayNameSingular?.toLowerCase() || 'item' }))) {
      return;
    }
    
    try {
      const service = getCurrentService();
      await service.delete(item.id);
      toast.success(`${getCurrentEntity().displayNameSingular} ${t('navigation.permanentlyDeletedSuccess')}`);
    } catch (error) {
      console.error('Error hard deleting item:', error);
      toast.error(t('navigation.failedToDeleteItem'));
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      const service = getCurrentService();
      
      if (editingItem) {
        await service.update(editingItem.id, data);
        toast.success(`${getCurrentEntity().displayNameSingular} ${t('navigation.updatedSuccess')}`);
      } else {
        await service.create(data);
        toast.success(`${getCurrentEntity().displayNameSingular} ${t('navigation.createdSuccess')}`);
      }
      
      // Reload dynamic options after creating/updating items
      await loadDynamicOptions();
      
      setShowForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(t('navigation.failedToSaveItem'));
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  const handleModuleSelectionChange = (selectedIds: string[]) => {
    setSelectedModuleIds(selectedIds);
  };

  const handleScreenSelectionChange = (selectedIds: string[]) => {
    setSelectedScreenIds(selectedIds);
  };

  const handleReorder = (_items: any[]) => {
    // The reordering is handled within the SortableNavigationTree component
  };

  const renderViewModeContent = () => {
    if (showForm) {
      return (
        <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-[var(--color-surface-text)]">
              {editingItem 
                ? t('navigation.editEntity', { entityName: getCurrentEntity().displayNameSingular || 'item' })
                : t('navigation.createEntity', { entityName: getCurrentEntity().displayNameSingular || 'item' })
              }
            </h2>
            <p className="text-[var(--color-secondary-text)] mt-2">
              {editingItem 
                ? t('navigation.updateNavigationItemDetails')
                : t('navigation.createNewNavigationItem')
              }
            </p>
          </div>

          <DynamicForm
            key={formKey}
            entity={getCurrentEntity()}
            initialData={editingItem}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            dynamicOptions={dynamicOptions}
          />
        </Card>
      );
    }

    switch (viewMode) {
      case 'tree':
        return (
          <Card className="p-6">
            <SortableNavigationTree
              onReorder={handleReorder}
              onEditItem={permissions.canUpdate() ? handleEdit : undefined}
            />
          </Card>
        );

      case 'modules':
        return (
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-[var(--color-surface-text)]">{t('navigation.navigationModules')}</h2>
            </div>
            
            {/* Selection Summary */}
            {selectedModuleIds.length > 0 && (
              <div className="mb-4 p-4 bg-[var(--color-info)]/20 border border-[var(--color-info)]/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-[var(--color-info)]">
                      {selectedModuleIds.length} module(s) selected
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSelectedModuleIds([])}
                      className="text-sm text-[var(--color-info)] hover:text-[var(--color-accent-bg)] underline"
                    >
                      Clear selection
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <FlexibleCRUDTable
              key="modules-table"
              entity={getCurrentEntity()}
              service={getCurrentService()}
              onEdit={permissions.canUpdate() ? handleEdit : undefined}
              onCopy={permissions.canUpdate() ? getCurrentService().copy : undefined}
              onCreate={permissions.canCreate() ? () => handleCreate('module') : undefined}
              enableRowSelection={true}
              onSelectionChange={handleModuleSelectionChange}
              customActions={[
                ...(permissions.canUpdate() ? [{
                  label: t('navigation.toggleStatus'),
                  icon: EyeIcon,
                  onClick: handleToggleStatus,
                  variant: 'secondary' as const
                }] : []),
                ...(permissions.canDelete() ? [{
                  label: t('navigation.hardDelete'),
                  icon: TrashIcon,
                  onClick: handleHardDelete,
                  variant: 'danger' as const
                }] : [])
              ]}
            />
          </Card>
        );

      case 'screens':
        return (
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-[var(--color-surface-text)]">{t('navigation.navigationScreens')}</h2>
            </div>
            
            {/* Selection Summary */}
            {selectedScreenIds.length > 0 && (
              <div className="mb-4 p-4 bg-[var(--color-info)]/20 border border-[var(--color-info)]/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-[var(--color-info)]">
                      {selectedScreenIds.length} screen(s) selected
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSelectedScreenIds([])}
                      className="text-sm text-[var(--color-info)] hover:text-[var(--color-accent-bg)] underline"
                    >
                      Clear selection
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <FlexibleCRUDTable
              key="screens-table"
              entity={getCurrentEntity()}
              service={getCurrentService()}
              onEdit={permissions.canUpdate() ? handleEdit : undefined}
              onCopy={permissions.canUpdate() ? getCurrentService().copy : undefined}
              onCreate={permissions.canCreate() ? () => handleCreate('screen') : undefined}
              enableRowSelection={true}
              onSelectionChange={handleScreenSelectionChange}
              customActions={[
                ...(permissions.canUpdate() ? [{
                  label: t('navigation.toggleStatus'),
                  icon: EyeIcon,
                  onClick: handleToggleStatus,
                  variant: 'secondary' as const
                }] : []),
                ...(permissions.canDelete() ? [{
                  label: t('navigation.hardDelete'),
                  icon: TrashIcon,
                  onClick: handleHardDelete,
                  variant: 'danger' as const
                }] : [])
              ]}
            />
          </Card>
        );

      case 'nodes':
        return (
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-[var(--color-surface-text)]">Navigation Nodes</h2>
            </div>
            
            <FlexibleCRUDTable
              key="nodes-table"
              entity={getCurrentEntity()}
              service={getCurrentService()}
              onEdit={permissions.canUpdate() ? handleEdit : undefined}
              onCopy={permissions.canUpdate() ? getCurrentService().copy : undefined}
              onCreate={permissions.canCreate() ? () => handleCreate('node') : undefined}
              enableRowSelection={true}
              pageSize={5}
              customActions={[
                {
                  label: 'Toggle Status',
                  icon: EyeIcon,
                  onClick: handleToggleStatus,
                  variant: 'secondary',
                },
                {
                  label: 'Hard Delete',
                  icon: TrashIcon,
                  onClick: handleHardDelete,
                  variant: 'danger',
                }
              ]}
            />
          </Card>
        );

      default:
        return null;
    }
  };

  // Access Control - Check if user has permission to view navigation management
  if (!permissions.canView()) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-[var(--color-error)]" />
          <h3 className="mt-2 text-sm font-medium text-[var(--color-surface-text)]">{t('navigation.accessDenied')}</h3>
          <p className="mt-1 text-sm text-[var(--color-secondary-text)]">{t('navigation.insufficientPermissions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">
          {t('navigation.navigationManagement')}
        </h1>
        <p className="text-[var(--color-secondary-text)]">
          {t('navigation.navigationManagementDescription')}
        </p>
        
        
      </div>

      {/* View Mode Tabs - Always show tabs */}
      <div className="mb-6">
        <div className="border-b border-[var(--color-border)]">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setViewMode('tree')}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm
                ${viewMode === 'tree'
                  ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                  : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                }
              `}
            >
              <ArrowsUpDownIcon className="w-4 h-4 inline mr-2" />
              {t('navigation.dragAndDropTree')}
            </button>
            <button
              onClick={() => {
                setViewMode('modules');
                setCurrentEntity('module');
              }}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm
                ${viewMode === 'modules'
                  ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                  : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                }
              `}
            >
              <TableCellsIcon className="w-4 h-4 inline mr-2" />
              {t('navigation.modules')}
            </button>
            <button
              onClick={() => {
                setViewMode('screens');
                setCurrentEntity('screen');
              }}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm
                ${viewMode === 'screens'
                  ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                  : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                }
              `}
            >
              <TableCellsIcon className="w-4 h-4 inline mr-2" />
              {t('navigation.screens')}
            </button>
            <button
              onClick={() => {
                setViewMode('nodes');
                setCurrentEntity('node');
              }}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm
                ${viewMode === 'nodes'
                  ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                  : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                }
              `}
            >
              <TableCellsIcon className="w-4 h-4 inline mr-2" />
              {t('navigation.nodes')}
            </button>
          </nav>
        </div>
      </div>

      {/* Quick Actions */}
      {viewMode === 'tree' && (
        <div className="mb-6">
          <Card className="p-4">
                          <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-[var(--color-surface-text)]">{t('navigation.quickActions')}</h3>
                  <p className="text-sm text-[var(--color-secondary-text)]">{t('navigation.createNewNavigationItems')}</p>
                </div>
                <div className="flex space-x-3">
                  <AccessControl requiredRole="PLATFORM_ADMIN" fallback={
                    <Button disabled variant="outline" className="opacity-50">
                      <PlusIcon className="w-4 h-4 mr-2" />
                      {t('navigation.addModule')} ({t('navigation.adminOnly')})
                    </Button>
                  }>
                    <Button onClick={() => handleCreate('module')} variant="outline">
                      <PlusIcon className="w-4 h-4 mr-2" />
                      {t('navigation.addModule')}
                    </Button>
                  </AccessControl>
                  
                  <AccessControl requiredRole="PLATFORM_ADMIN" fallback={
                    <Button disabled variant="outline" className="opacity-50">
                      <PlusIcon className="w-4 h-4 mr-2" />
                      {t('navigation.addScreen')} ({t('navigation.adminOnly')})
                    </Button>
                  }>
                    <Button onClick={() => handleCreate('screen')} variant="outline">
                      <PlusIcon className="w-4 h-4 mr-2" />
                      {t('navigation.addScreen')}
                    </Button>
                  </AccessControl>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Main Content */}
      {renderViewModeContent()}
    </div>
  );
};

export default NavigationManagementPage;
